import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { ExternalHoldingsFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { NumberInputProps } from "models/propTypes/wintFormPropsTypes";
import { ExternalCompanyHoldingsFormProps } from "models/propTypes/externalCompanyHoldingsPropTypes";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import {
  TextInput,
  NumberInput,
  EditableTable,
  useWintForm,
  VerticalFormField,
  WintForm,
} from "components/common/wintForms";
import { Grid, Typography } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, PersonNumberHelper } from "utils/helpers";
import { CommonConstants } from "utils/constants";
import cssExports from "theme/styles/main.module.scss";

const ExternalCompanyHoldingsForm: ForwardRefRenderFunction<FormHandles, ExternalCompanyHoldingsFormProps> = (
  props,
  ref
) => {
  const { wintFormProps, connect, values } = useWintForm<ExternalHoldingsFormTypes.Declaration>({
    formRef: ref,
    defaultValues: props.defaultValues,
    validationSchema: props.validationSchema,
    handleSubmit: props.handleSubmit,
    onFormValidationChange: props.onFormValidationChange,
  });

  const spousePersonalNumberProps = connect("spousePersonalNumber");
  const externalHoldingsProps = connect("externalHoldings");

  // Check if the entered value is a coordination number and show warning
  const coordinationNumberWarning = React.useMemo(() => {
    const personalNumber = values.spousePersonalNumber;
    if (!personalNumber || !PersonNumberHelper.isValidFormat(personalNumber)) {
      return undefined;
    }

    if (PersonNumberHelper.isCoordinationNumber(personalNumber)) {
      return {
        type: StyleEnums.DisplayType.Warning,
        message: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.ExternalCompanyHoldings.SpouseCoordinationNumberWarning
        ),
      };
    }

    return undefined;
  }, [values.spousePersonalNumber]);

  return (
    <WintForm {...wintFormProps}>
      <Grid container rowGap={1}>
        {/* External Holdings Section */}
        <Grid item container>
          <Grid item container marginBottom={2}>
            <Typography className={cssExports.sectionTitle} marginTop={1}>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.ExternalCompanyHoldings.AddExternalHoldings
              )}
            </Typography>
            <Typography className={cssExports.description} marginTop={1}>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.ExternalCompanyHoldings.AddExternalHoldingsDescription
              )}
            </Typography>
          </Grid>

          <EditableTable<ExternalHoldingsFormTypes.ExternalHolding>
            className={cssExports.ownershipDetailsTable}
            connect={connect as any}
            name="externalHoldings"
            value={externalHoldingsProps.value ?? []}
            isCompactTable={true}
            fields={{
              companyName: {
                Component: TextInput,
                extraProps: {
                  label: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.ExternalCompanyHoldings.CompanyName
                  ),
                  placeholder: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.ExternalCompanyHoldings.CompanyName
                  ),
                },
                widthColumnCount: 6,
              },
              ownership: {
                Component: NumberInput,
                extraProps: {
                  label: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.ExternalCompanyHoldings.Ownership
                  ),
                  placeholder: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.ExternalCompanyHoldings.Ownership
                  ),
                  suffix: "%",
                  decimalPoints: 2,
                  allowNegative: false,
                  max: 100,
                },
                widthColumnCount: 4,
              },
            }}
            defaultRow={{ companyName: "", ownership: 0 }}
          />
        </Grid>
        {/* Spouse Personal Number Field */}
        <Grid item container>
          <Grid item container marginBottom={1}>
            <Typography className={cssExports.sectionTitle} marginTop={1}>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.ExternalCompanyHoldings.SpousePersonalNumberTitle
              )}
            </Typography>
            <Typography className={cssExports.description} marginTop={1}>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.ExternalCompanyHoldings.SpousePersonalNumberDescription
              )}
            </Typography>
          </Grid>
          <VerticalFormField<NumberInputProps>
            Component={NumberInput}
            {...spousePersonalNumberProps}
            label={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.ExternalCompanyHoldings.SpousePersonalNumberPlaceholder
            )}
            allowLeadingZeros
            format={CommonConstants.PersonNumberLongMask}
            customMessage={coordinationNumberWarning}
          />
          <Typography className={cssExports.formatText} marginTop={1}>
            {LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.ExternalCompanyHoldings.SpousePersonalNumberFormat
            )}
          </Typography>
        </Grid>
      </Grid>
    </WintForm>
  );
};

export default forwardRef(ExternalCompanyHoldingsForm);
